import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Trash2 } from 'lucide-react';
import axios from 'axios';
import AutoRefreshControl from '../components/AutoRefreshControl';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Tooltip } from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = 'http://localhost:8765';

function WorkerLogsPage() {
  const { workerId } = useParams();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    level: '',
    category: '',
    hours: 24,
    search: '',
  });

  const fetchLogs = async () => {
    try {
      const params = { hours: filter.hours, limit: 500 };
      if (filter.level) params.level = filter.level;
      if (filter.category) params.category = filter.category;
      const response = await axios.get(`${API_URL}/api/logs/worker/${workerId}`, { params });
      setLogs(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/logs/worker/${workerId}/stats`, {
        params: { hours: filter.hours },
      });
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const loadAllData = async () => {
    await Promise.all([fetchLogs(), fetchStats()]);
  };

  const autoRefresh = useAutoRefresh(loadAllData, 30000, true);

  useEffect(() => {
    loadAllData();
  }, [workerId, filter]);

  const getLevelVariant = (level) => {
    const colors = {
      DEBUG: 'outline',
      INFO: 'secondary',
      WARNING: 'warning',
      ERROR: 'destructive',
      CRITICAL: 'destructive',
    };
    return colors[level] || 'outline';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      startup: '🚀',
      monitoring: '👁️',
      update: '🔄',
      network: '🌐',
      error: '❌',
      security: '🔒',
      system: '⚙️',
    };
    return icons[category] || '📝';
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Clear logs older than 30 days?')) return;
    try {
      await axios.delete(`${API_URL}/api/logs/worker/${workerId}?older_than_days=30`);
      alert('Old logs cleared successfully');
      fetchLogs();
    } catch (error) {
      alert('Failed to clear logs');
    }
  };

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'Level', 'Category', 'Message'].join(','),
      ...logs.map((log) =>
        [log.timestamp, log.level, log.category, `"${log.message.replace(/"/g, '""')}"`].join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `worker-${workerId}-logs-${new Date().toISOString()}.csv`;
    a.click();
  };

  const filteredLogs = logs.filter((log) => {
    if (filter.search && !log.message.toLowerCase().includes(filter.search.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Worker Logs"
        actions={
          <>
            <AutoRefreshControl {...autoRefresh} />
            <Tooltip label="Export CSV">
              <Button variant="outline" size="icon-sm" onClick={handleExport}>
                <Download />
              </Button>
            </Tooltip>
            <Tooltip label="Clear Old Logs">
              <Button
                variant="outline"
                size="icon-sm"
                className="text-destructive hover:text-destructive"
                onClick={handleClearLogs}
              >
                <Trash2 />
              </Button>
            </Tooltip>
          </>
        }
      />

      {stats && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Logs by Level</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.by_level || {}).map(([level, count]) => (
                  <Badge key={level} variant={getLevelVariant(level)}>
                    {level}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Logs by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.by_category || {}).map(([category, count]) => (
                  <Badge key={category} variant="outline">
                    {getCategoryIcon(category)} {category}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {stats && stats.recent_errors && stats.recent_errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>Recent Errors</AlertTitle>
          <AlertDescription>
            {stats.recent_errors.slice(0, 3).map((error, idx) => (
              <div key={idx} className="text-xs">
                [{new Date(error.timestamp).toLocaleString()}] {error.message}
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4">
            <Select
              value={filter.level}
              onChange={(e) => setFilter({ ...filter, level: e.target.value })}
            >
              <option value="">All Levels</option>
              <option value="DEBUG">DEBUG</option>
              <option value="INFO">INFO</option>
              <option value="WARNING">WARNING</option>
              <option value="ERROR">ERROR</option>
              <option value="CRITICAL">CRITICAL</option>
            </Select>
            <Select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            >
              <option value="">All Categories</option>
              <option value="startup">Startup</option>
              <option value="monitoring">Monitoring</option>
              <option value="update">Update</option>
              <option value="network">Network</option>
              <option value="error">Error</option>
              <option value="security">Security</option>
              <option value="system">System</option>
            </Select>
            <Select
              value={filter.hours}
              onChange={(e) => setFilter({ ...filter, hours: Number(e.target.value) })}
            >
              <option value={1}>Last Hour</option>
              <option value={6}>Last 6 Hours</option>
              <option value={24}>Last 24 Hours</option>
              <option value={72}>Last 3 Days</option>
              <option value={168}>Last Week</option>
            </Select>
            <Input
              placeholder="Search message..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Logs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[600px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead className="w-[100px]">Level</TableHead>
                  <TableHead className="w-[120px]">Category</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getLevelVariant(log.level)}>{log.level}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {getCategoryIcon(log.category)} {log.category}
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-xs text-foreground">{log.message}</div>
                      {log.details && (
                        <div className="mt-1 text-xs text-muted-foreground">{log.details}</div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="p-4 text-sm text-muted-foreground">
            Showing {filteredLogs.length} of {logs.length} logs
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default WorkerLogsPage;
